import { h } from "preact";
import { useRef, useState, useEffect } from "preact/hooks";

import { getUserStatus, createUser } from "../../../../../foundation/api";
import { handleError } from "../../../../../foundation/errors/errors";
import ClientError from "../../../../../foundation/errors/clientError";
import Mailcheck from "../../../../../foundation/mailcheck";
import Cookies from "../../../../../foundation/cookies";
import { WEB_URL } from "../../../../../foundation/constants";
import { urlResolve } from "../../../../../foundation/utils/url";
import { sendSignup } from "../../../../../foundation/utils/tracking";
import { stopPropagation } from "../../../../../foundation/utils/legacy";
import analyzePasswordStrength from "../../../../../foundation/utils/analyzePasswordStrength";
import getRedirectUrl from "../../../../../foundation/utils/get-redirect-url";
import * as dialog from "../../../../../foundation/dialog";
import SignupParams from "../../types/SignupParams";
import FormFieldStatus from "../../types/FormFieldStatus";
import SignupCredentials from "../../types/SignupCredentials";
import MailcheckSuggestion from "../../types/MailcheckSuggestion";

import TextFormField from "../../../../../library/FormField/TextFormField";
import Button from "../../../../../library/Button";
import Checkbox from "../../../../../library/Checkbox";
import TipText from "../TipText/TipText";
import ToggleShowPassword from "../../ToggleShowPassword";
import { handleSSOLoginSignup } from "../../../../../foundation/utils/saml";
import getAdParameters from "../../../../../foundation/utils/signup/getAdParameters";
import getAnonymousUserId from "../../../../../foundation/utils/signup/getAnonymousUserId";
import getMixpanelUserId from "../../../../../foundation/utils/signup/getMixpanelUserId";

import "./style.css";

interface Props {
    signupParams?: SignupParams;
    signupToken?: string;
    onHandleChange: (value: string) => void;
}

function SignupForm({
    signupParams,
    signupToken,
    onHandleChange
}: Props) {
    const [email, setEmail] = useState(signupParams?.email || "");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [emailStatus, setEmailStatus] = useState<FormFieldStatus>({ type: "empty" });
    const [passwordStatus, setPasswordStatus] = useState<FormFieldStatus>({ type: "empty" });
    const [agreedTerms, setAgreedTerms] = useState<boolean>(false);
    const [submittingSignupForm, setSubmittingSignupForm] = useState<boolean>(false);
    const emailElement = useRef<HTMLInputElement>(null);
    const passwordElement = useRef<HTMLInputElement>(null);
    const termsLabelElement = useRef<HTMLLabelElement>(null);
    const emailTimeoutRef = useRef<number>();

    useEffect(() => {
        onHandleChange(email);
    }, [email]);

    function handleFocus() {
        if (!email) {
            setEmailStatus({ type: "tip", explanation: "We'll send you an email to verify the address." });
            emailTimeoutRef.current = window.setTimeout(() => clearEmailTipText(), 5000);
            return;
        }
        checkEmailStatus(email);
    }

    function clearEmailTipText() {
        checkEmailStatus(email);
    }

    function handleEmailChange(event: Event) {
        const { value } = event.target as HTMLInputElement;
        setEmail(value);
        clearTimeout(emailTimeoutRef.current);
        emailTimeoutRef.current = window.setTimeout(() => checkEmailStatus(value), 1000);
    }

    function handleEmailSuggestionClick(event: Event, emailSuggestion: string) {
        event.preventDefault();
        setEmail(emailSuggestion);
        checkEmailStatus(emailSuggestion);
    }

    async function checkEmailStatus(value: string) {
        if (!value) {
            setEmailStatus({ type: "empty" });
            return;
        }

        if (!emailElement.current!.checkValidity()) {
            setEmailStatus({
                type: "error",
                explanation: "Your email doesn't look right!"
            });
            return;
        }

        try {
            const { status: userStatus } = await getUserStatus(value);
            const loginQueryParams = { ...signupParams, email: value };
            if (userStatus === "verified" || userStatus === "registered") {
                setEmailStatus({
                    type: "warning",
                    explanation: (
                        <span>
                            This email is already registered.<br/>
                            Want to <a href={urlResolve("login", { query: loginQueryParams })}>Log in</a> instead?
                        </span>
                    )
                });
            } else if (userStatus === "invited") {
                if (signupToken) {
                    if (signupParams?.email === value) {
                        setEmailStatus({ type: "success", explanation: "Looks good!" });
                    } else {
                        setEmailStatus({ type: "warning", explanation: "Looks like you’re signing up with a different email, please request invites to projects again." });
                    }
                } else {
                    setEmailStatus({ type: "suggestion", explanation: "Looks like you were invited! Follow invitation email to access your projects right away." });
                }
            }
        } catch (error) {
            handleError(error, {
                clientErrorHandler: {
                    404: () => {
                        if (signupParams?.email !== value && signupToken) {
                            setEmailStatus({ type: "warning", explanation: "Looks like you’re signing up with a different email, please request invites to projects again." });
                        } else {
                            Mailcheck.run({
                                email: value,
                                suggested: (suggestion: MailcheckSuggestion) => {
                                    setEmailStatus({
                                        type: "suggestion",
                                        explanation: (
                                            <span>
                                                Did you mean&nbsp;
                                                {/* eslint-disable-next-line react/jsx-no-bind */}
                                                <a href="#" onClick={ev => handleEmailSuggestionClick(ev, suggestion.full)}>
                                                    {suggestion.full}
                                                </a>?
                                            </span>
                                        )
                                    });
                                },
                                empty: () => {
                                    setEmailStatus({ type: "success", explanation: "Looks good!" });
                                }
                            });
                        }
                    }
                }
            });
        }
    }

    function handlePasswordChange(event: Event) {
        const { value } = event.target as HTMLInputElement;
        setPassword(value);

        if (value.length < 8) {
            setPasswordStatus({ type: "tip", explanation: "Type 8 characters or more" });
            return;
        }

        const userInputs = email.split(/[@.+_-]+/);
        const { weak, warning } = analyzePasswordStrength(value, userInputs);
        if (warning) {
            setPasswordStatus({
                type: weak ? "error" : "suggestion",
                explanation: weak ? `Password too weak: ${warning}` : warning
            });
            return;
        }

        setPasswordStatus({ type: "success", explanation: "Looks good!" });
    }

    function toggleShowPassword() {
        setShowPassword(!showPassword);
    }

    function toggleAgreedTerms() {
        setAgreedTerms(!agreedTerms);
    }

    function handleBlur() {
        checkEmailStatus(email);
    }

    async function handleSignup(event: Event) {
        event.preventDefault();

        if (!isFormValid()) {
            return;
        }

        setSubmittingSignupForm(true);

        const credentials: SignupCredentials = {
            email,
            password,
            anonymousUserId: getAnonymousUserId(),
            mixpanelUserId: getMixpanelUserId(),
            utm: signupParams?.utm,
            ad: getAdParameters(),
            signupMethod: "Email"
        };

        const headers = new Headers({
            "Content-Type": "application/json"
        });

        if (signupParams?.email === email && signupToken) {
            headers.set("Zeplin-Token", signupToken);
        }

        try {
            const response = await createUser(credentials, headers);

            if (typeof response.ssoLoginParams !== "undefined") {
                handleSSOLoginSignup(response.ssoLoginParams, credentials.email, { isSignup: true });
                return;
            }

            if (response.mfaSetupRequired && response.token) {
                Zeplin.headers.set("Zeplin-Token", response.token);
                Cookies.set("mfaToken", response.token, {});
                Cookies.remove("userToken");
            } else {
                Cookies.set("userToken", response.token, {});
            }

            Cookies.remove("utmParams");
            delete credentials.password;

            credentials._id = response._id;

            credentials.status = signupParams?.email === email && signupToken
                ? "verified"
                : "registered";

            credentials.paymentPlan = "free";
            localStorage.setItem("user", JSON.stringify(credentials));
            sessionStorage.setItem("mxShouldAlias", "true");
            if (response.accountCaptureInfo) {
                sessionStorage.setItem("accountCaptureInfo", JSON.stringify(response.accountCaptureInfo));
            }
            sendSignup();
            if (response.mfaSetupRequired) {
                sessionStorage.setItem("redirectTo", getRedirectUrl(urlResolve("signupComplete"), "/"));
                location.assign(urlResolve("multi-factor-authentication-setup"));
            } else {
                location.assign(urlResolve("signupComplete"));
            }
        } catch (error) {
            handleError(error, {
                clientErrorHandler: {
                    428() {
                        location.assign(urlResolve("signup-sso", { query: { email, token: signupToken } }));
                    },
                    default() {
                        setSubmittingSignupForm(false);
                        const { title, message } = error as ClientError;

                        dialog.create({ title, message });
                    }
                }
            });
        }
    }

    function isFormValid() {
        const statusTypesBlockingSubmit = ["error", "warning", "empty"];
        if (statusTypesBlockingSubmit.includes(emailStatus.type) ||
            statusTypesBlockingSubmit.includes(passwordStatus.type)) {
            return false;
        }
        if (!agreedTerms) {
            return false;
        }

        return !!email;
    }

    return (
        <form class="signupForm" onSubmit={handleSignup}>
            <TextFormField
                class="signupFormField"
                name="Email"
                value={email}
                placeholder="Work email"
                nativeProps={{
                    class: "signupFormInput",
                    autocapitalize: "off",
                    autocomplete: "off",
                    type: "email",
                    required: true,
                    ref: emailElement,
                    onInput: handleEmailChange
                }}
                maxLength={254}
                onFocus={handleFocus}
                onBlur={handleBlur}
                shouldDisplayCountDown={false}
                shouldAlwaysDisplayTipText={true}
                tipText={<TipText type={emailStatus.type} explanation={emailStatus.explanation}/>}
                autoFocus/>
            <TextFormField
                class="signupFormField"
                name="Password"
                value={password}
                placeholder="Password"
                nativeProps={{
                    class: "signupFormInput",
                    type: showPassword ? "text" : "password",
                    pattern: ".{8,100}",
                    required: true,
                    minlength: 8,
                    autocomplete: "new-password",
                    autocapitalize: "off",
                    ref: passwordElement,
                    onInput: handlePasswordChange
                }}
                adornment={!!password && (
                    <ToggleShowPassword
                        showPassword={showPassword}
                        onClick={toggleShowPassword}/>
                )}
                maxLength={100}
                shouldDisplayCountDown={false}
                shouldAlwaysDisplayTipText={true}
                tipText={<TipText type={passwordStatus.type} explanation={passwordStatus.explanation}/>}/>
            <label class="termsOfService" ref={termsLabelElement}>
                <Checkbox
                    checked={agreedTerms}
                    tabIndex={0}
                    onChange={toggleAgreedTerms}/>
                <span
                    class="label ellipsis"
                    title="I agree to Terms of Service">
                    I agree to
                    <a
                        href={`${WEB_URL}/terms`}
                        onMouseDown={stopPropagation}
                        target="_blank"
                        rel="noopener noreferrer">
                        <strong> Terms of Service</strong>
                    </a>
                </span>
            </label>
            <Button
                disabled={!isFormValid() || submittingSignupForm}
                class="signupFormButton"
                type="submit"
                primary>
                Sign up for free
            </Button>
        </form>
    );
}

export default SignupForm;
